/**
 * Per-row mutation state — which row a shared mutation is actually acting on.
 *
 * **Lives in `utils/` rather than next to any one caller (#1312).** It started
 * in `components/shared-view/sharedViewModel.ts` because "Shared with me" was
 * the first surface to need it (#1205), but the rule is not about shares: it
 * belongs to every list that renders a per-row button from ONE `useMutation`.
 * There are six such call sites across four directories now — the two share
 * dialogs, the shared view, the admin release table and the two Downloads
 * surfaces — so importing it from a sibling feature's view-model made the
 * shared-view directory look like the owner of a rule the rest of the app has
 * to follow.
 */

/**
 * Which row a mutation is in flight for, or `undefined` when none is.
 *
 * One mutation instance serves the whole list, so its `isPending` is a property
 * of the LIST rather than of a row. Wired straight to every button it disables
 * all of them the moment any one is clicked — including rows belonging to a
 * different organization — and a second click during a slow request does
 * nothing. That was #1205 for Download and #1232 for Revoke, and Revoke is the
 * sharper case: an admin who cannot withdraw the second grant because the first
 * succeeded is an operational problem, not an annoyance.
 *
 * **`isPending` is checked first, and that ordering is the whole function.**
 * react-query keeps the last `variables` after a mutation settles, so reading it
 * unconditionally would leave the most recently acted-on row disabled
 * permanently — a worse bug than the one it replaces, and one that appears only
 * after a *successful* call, which is the path least likely to be exercised
 * while testing a button. #1232 names this trap explicitly:
 *
 *     disabled={revoke.variables === entry.shareId && revoke.isPending}  // trap
 *
 * is correct only by accident of `&&` evaluation order, and the shape people
 * actually write is the other way round.
 *
 * The id it returns is whatever the mutation takes as its `variables` — a share
 * id on the share surfaces, a release id on Downloads and the admin release
 * table. Any list whose rows carry a stable id can use it; the only requirement
 * is that the mutation's variables ARE that id, so a mutation taking an object
 * has to be compared on a field rather than through this helper.
 */
export function pendingTargetId(
  isPending: boolean,
  variables: string | undefined
): string | undefined {
  return isPending ? variables : undefined;
}
